import {
    Box,
    Button,
    Card,
    Chip,
    Grid,
    IconButton,
    TextField,
    Typography,
    alpha,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
} from "@mui/material";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Loader from "../../../components/Loader";
import {
    socialMediaService,
    PLATFORM_INFO,
    SocialPost,
} from "../../../api/services/socialMediaService";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import ThumbUpRoundedIcon from "@mui/icons-material/ThumbUpRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";

const premiumCard = {
    borderRadius: "16px",
    border: "1px solid rgba(226, 232, 240, 0.6)",
    boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.02)",
    overflow: "hidden",
};

const postStatusConfig: Record<
    string,
    { bg: string; color: string; label: string; icon: any }
> = {
    draft: {
        bg: "#F1F5F9",
        color: "#64748B",
        label: "Draft",
        icon: EditRoundedIcon,
    },
    approved: {
        bg: "#F0FDF4",
        color: "#16A34A",
        label: "Approved",
        icon: CheckCircleRoundedIcon,
    },
    scheduled: {
        bg: "#EFF6FF",
        color: "#2563EB",
        label: "Scheduled",
        icon: ScheduleRoundedIcon,
    },
    queued: {
        bg: "#FFF7ED",
        color: "#EA580C",
        label: "Queued",
        icon: ScheduleRoundedIcon,
    },
    posting: {
        bg: "#EFF6FF",
        color: "#2563EB",
        label: "Posting",
        icon: SendRoundedIcon,
    },
    posted: {
        bg: "#F0FDF4",
        color: "#16A34A",
        label: "Posted",
        icon: CheckCircleRoundedIcon,
    },
    failed: {
        bg: "#FEF2F2",
        color: "#DC2626",
        label: "Failed",
        icon: ErrorRoundedIcon,
    },
};

const campaignStatusConfig: Record<
    string,
    { bg: string; color: string; label: string }
> = {
    draft: { bg: "#F1F5F9", color: "#64748B", label: "Draft" },
    generating: { bg: "#EFF6FF", color: "#2563EB", label: "Generating" },
    ready: { bg: "#FFF7ED", color: "#EA580C", label: "Ready" },
    approved: { bg: "#F0FDF4", color: "#16A34A", label: "Approved" },
    posting: { bg: "#EFF6FF", color: "#2563EB", label: "Posting" },
    completed: { bg: "#F0FDF4", color: "#16A34A", label: "Completed" },
    partial: { bg: "#FFFBEB", color: "#D97706", label: "Partial" },
    failed: { bg: "#FEF2F2", color: "#DC2626", label: "Failed" },
};

const SocialCampaignDetail = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [editPost, setEditPost] = useState<SocialPost | null>(null);
    const [editContent, setEditContent] = useState("");

    const { data, isLoading } = useQuery(
        ["social-campaign", id],
        () => socialMediaService.getCampaign(Number(id)),
        { enabled: !!id }
    );

    const approveMutation = useMutation(
        () => socialMediaService.approveCampaign(Number(id)),
        {
            onSuccess: () => {
                toast.success("Campaign approved!");
                queryClient.invalidateQueries(["social-campaign", id]);
            },
            onError: () => {
                toast.error("Failed to approve campaign");
            },
        }
    );

    const dispatchMutation = useMutation(
        () => socialMediaService.dispatchCampaign(Number(id)),
        {
            onSuccess: (res) => {
                const { success, failed } = res.data || {};
                toast.success(`Dispatched: ${success} success, ${failed} failed`);
                queryClient.invalidateQueries(["social-campaign", id]);
            },
            onError: () => {
                toast.error("Failed to dispatch campaign");
            },
        }
    );

    const updatePostMutation = useMutation(
        ({ postId, data }: { postId: number; data: any }) =>
            socialMediaService.updatePost(postId, data),
        {
            onSuccess: () => {
                toast.success("Post updated");
                queryClient.invalidateQueries(["social-campaign", id]);
                setEditPost(null);
            },
            onError: () => {
                toast.error("Failed to update post");
            },
        }
    );

    const dispatchPostMutation = useMutation(
        (postId: number) => socialMediaService.dispatchPost(postId),
        {
            onSuccess: () => {
                toast.success("Post dispatched!");
                queryClient.invalidateQueries(["social-campaign", id]);
            },
            onError: () => {
                toast.error("Failed to dispatch post");
            },
        }
    );

    if (isLoading) return <Loader />;

    const campaign = data?.data;
    if (!campaign) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography>Campaign not found</Typography>
            </Box>
        );
    }

    const status =
        campaignStatusConfig[campaign.status] || campaignStatusConfig.draft;
    const posts: SocialPost[] = campaign.posts || [];
    const canApprove = ["draft", "ready"].includes(campaign.status);
    const canDispatch = ["approved"].includes(campaign.status);

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box display="flex" alignItems="center" gap={1} mb={1}>
                <IconButton onClick={() => navigate("/social-media/campaigns")}>
                    <ArrowBackRoundedIcon />
                </IconButton>
                <Box flex={1}>
                    <Box display="flex" alignItems="center" gap={1.5}>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: "#0F172A" }}>
                            {campaign.title}
                        </Typography>
                        <Chip
                            label={status.label}
                            size="small"
                            sx={{
                                backgroundColor: status.bg,
                                color: status.color,
                                fontWeight: 600,
                            }}
                        />
                    </Box>
                    <Typography sx={{ color: "#64748B", mt: 0.5, fontSize: "13px" }}>
                        {campaign.source_type === "blog"
                            ? `Generated from blog article #${campaign.source_id}`
                            : "Generated from AI prompt"}{" "}
                        · Created {new Date(campaign.created_at).toLocaleDateString()}
                    </Typography>
                </Box>
                <Box display="flex" gap={1}>
                    {canApprove && (
                        <Button
                            variant="outlined"
                            color="success"
                            startIcon={<CheckCircleRoundedIcon />}
                            onClick={() => approveMutation.mutate()}
                            disabled={approveMutation.isLoading}
                        >
                            Approve All
                        </Button>
                    )}
                    {canDispatch && (
                        <Button
                            variant="contained"
                            startIcon={<SendRoundedIcon />}
                            onClick={() => dispatchMutation.mutate()}
                            disabled={dispatchMutation.isLoading}
                        >
                            {dispatchMutation.isLoading ? "Dispatching..." : "Publish All"}
                        </Button>
                    )}
                </Box>
            </Box>

            {/* AI Prompt Info */}
            {campaign.ai_prompt && (
                <Card sx={{ ...premiumCard, mb: 3, p: 2.5 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <AutoAwesomeRoundedIcon sx={{ fontSize: 18, color: "#7C3AED" }} />
                        <Typography sx={{ fontWeight: 600, fontSize: "13px" }}>
                            AI Prompt
                        </Typography>
                    </Box>
                    <Typography
                        sx={{ fontSize: "13px", color: "#475569", lineHeight: 1.6 }}
                    >
                        {campaign.ai_prompt}
                    </Typography>
                </Card>
            )}

            {/* Posts Grid */}
            <Typography
                variant="h6"
                sx={{ fontWeight: 700, mb: 2, color: "#1E293B" }}
            >
                Platform Posts ({posts.length})
            </Typography>

            <Grid container spacing={2}>
                {posts.map((post) => {
                    const platformKey = post.platform?.platform || "unknown";
                    const info = PLATFORM_INFO[platformKey] || {
                        name: "Unknown",
                        color: "#666",
                        icon: "?",
                    };
                    const pStatus =
                        postStatusConfig[post.status] || postStatusConfig.draft;
                    const StatusIcon = pStatus.icon;

                    return (
                        <Grid item xs={12} md={6} key={post.id}>
                            <Card sx={premiumCard}>
                                {/* Platform Header */}
                                <Box
                                    sx={{
                                        px: 2.5,
                                        py: 1.5,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        borderBottom: "1px solid rgba(226, 232, 240, 0.5)",
                                        background: `linear-gradient(135deg, ${alpha(
                                            info.color,
                                            0.02
                                        )} 0%, ${alpha(info.color, 0.06)} 100%)`,
                                    }}
                                >
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Box
                                            sx={{
                                                width: 32,
                                                height: 32,
                                                borderRadius: "8px",
                                                backgroundColor: alpha(info.color, 0.1),
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontSize: "16px",
                                            }}
                                        >
                                            {info.icon}
                                        </Box>
                                        <Typography sx={{ fontWeight: 600, fontSize: "14px" }}>
                                            {info.name}
                                        </Typography>
                                        <Chip
                                            label={post.post_type}
                                            size="small"
                                            variant="outlined"
                                            sx={{ fontSize: "10px" }}
                                        />
                                    </Box>
                                    <Chip
                                        icon={<StatusIcon sx={{ fontSize: 14 }} />}
                                        label={pStatus.label}
                                        size="small"
                                        sx={{
                                            backgroundColor: pStatus.bg,
                                            color: pStatus.color,
                                            fontWeight: 600,
                                            fontSize: "11px",
                                        }}
                                    />
                                </Box>

                                {/* Content */}
                                <Box sx={{ p: 2.5 }}>
                                    <Typography
                                        sx={{
                                            fontSize: "13px",
                                            color: "#334155",
                                            lineHeight: 1.6,
                                            whiteSpace: "pre-wrap",
                                            maxHeight: 160,
                                            overflow: "auto",
                                            mb: 2,
                                        }}
                                    >
                                        {post.content}
                                    </Typography>

                                    {/* Media */}
                                    {post.media_urls && post.media_urls.length > 0 && (
                                        <Box display="flex" gap={1} mb={2} flexWrap="wrap">
                                            {post.media_urls.map((url, i) => (
                                                <Box
                                                    key={i}
                                                    component="img"
                                                    src={url}
                                                    sx={{
                                                        width: 80,
                                                        height: 80,
                                                        borderRadius: "8px",
                                                        objectFit: "cover",
                                                        border: "1px solid rgba(226,232,240,0.6)",
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    )}

                                    {/* Analytics (if posted) */}
                                    {post.status === "posted" && (
                                        <Box
                                            display="flex"
                                            gap={2}
                                            sx={{
                                                p: 1.5,
                                                borderRadius: "10px",
                                                backgroundColor: "#F8FAFC",
                                                mb: 2,
                                            }}
                                        >
                                            <Box display="flex" alignItems="center" gap={0.5}>
                                                <VisibilityRoundedIcon
                                                    sx={{ fontSize: 14, color: "#94A3B8" }}
                                                />
                                                <Typography sx={{ fontSize: "12px", fontWeight: 600 }}>
                                                    {new Intl.NumberFormat().format(
                                                        post.impressions || 0
                                                    )}
                                                </Typography>
                                            </Box>
                                            <Box display="flex" alignItems="center" gap={0.5}>
                                                <ThumbUpRoundedIcon
                                                    sx={{ fontSize: 14, color: "#94A3B8" }}
                                                />
                                                <Typography sx={{ fontSize: "12px", fontWeight: 600 }}>
                                                    {new Intl.NumberFormat().format(post.likes || 0)}
                                                </Typography>
                                            </Box>
                                            <Typography
                                                sx={{ fontSize: "12px", color: "#94A3B8" }}
                                            >
                                                {post.engagements || 0} eng · {post.clicks || 0} clicks
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* Error */}
                                    {post.error_message && (
                                        <Box
                                            sx={{
                                                p: 1.5,
                                                borderRadius: "10px",
                                                backgroundColor: "#FEF2F2",
                                                mb: 2,
                                            }}
                                        >
                                            <Typography
                                                sx={{ fontSize: "12px", color: "#DC2626" }}
                                            >
                                                {post.error_message}
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* Platform URL */}
                                    {post.platform_url && (
                                        <Box mb={1}>
                                            <Button
                                                size="small"
                                                startIcon={<OpenInNewRoundedIcon sx={{ fontSize: 14 }} />}
                                                href={post.platform_url}
                                                target="_blank"
                                                sx={{ fontSize: "12px" }}
                                            >
                                                View on {info.name}
                                            </Button>
                                        </Box>
                                    )}

                                    <Divider sx={{ my: 1 }} />

                                    {/* Actions */}
                                    <Box display="flex" gap={1} justifyContent="flex-end">
                                        {["draft", "approved", "ready"].includes(post.status) && (
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<EditRoundedIcon sx={{ fontSize: 14 }} />}
                                                onClick={() => {
                                                    setEditPost(post);
                                                    setEditContent(post.content);
                                                }}
                                            >
                                                Edit
                                            </Button>
                                        )}
                                        {post.status === "approved" && (
                                            <Button
                                                size="small"
                                                variant="contained"
                                                startIcon={<SendRoundedIcon sx={{ fontSize: 14 }} />}
                                                onClick={() => dispatchPostMutation.mutate(post.id)}
                                                disabled={dispatchPostMutation.isLoading}
                                            >
                                                Publish
                                            </Button>
                                        )}
                                        {post.status === "failed" && (
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                color="warning"
                                                startIcon={<ReplayRoundedIcon sx={{ fontSize: 14 }} />}
                                                onClick={() =>
                                                    updatePostMutation.mutate({
                                                        postId: post.id,
                                                        data: { status: "approved" },
                                                    })
                                                }
                                            >
                                                Retry
                                            </Button>
                                        )}
                                    </Box>
                                </Box>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>

            {/* Edit Dialog */}
            <Dialog
                open={!!editPost}
                onClose={() => setEditPost(null)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Edit Post -{" "}
                    {PLATFORM_INFO[editPost?.platform?.platform || ""]?.name ||
                        editPost?.platform?.platform}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        multiline
                        rows={8}
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        sx={{ mt: 1 }}
                        placeholder="Post content..."
                    />
                    <Typography
                        sx={{ fontSize: "12px", color: "#94A3B8", mt: 1, textAlign: "right" }}
                    >
                        {editContent.length} characters
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditPost(null)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            if (editPost) {
                                updatePostMutation.mutate({
                                    postId: editPost.id,
                                    data: { content: editContent },
                                });
                            }
                        }}
                        disabled={updatePostMutation.isLoading}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SocialCampaignDetail;
